import type { Location, Settings } from '../store/settings';

// Open-Meteo (no key). NOTE: the free tier is for non-commercial use – see CLAUDE.md to-dos.

export interface CurrentWeather {
  temperature: number;
  feelsLike: number;
  code: number;
  isDay: boolean;
  humidity: number;
  wind: number;
  windDirection: number;
}

export interface HourlyForecast {
  time: string; // local ISO (no zone) as returned with timezone=auto
  temperature: number;
  code: number;
  rainChance: number;
  isDay: boolean;
}

export interface DailyForecast {
  date: string;
  code: number;
  max: number;
  min: number;
  rainChance: number;
  uv: number;
  sunrise: string;
  sunset: string;
}

export interface Forecast {
  current: CurrentWeather;
  hourly: HourlyForecast[];
  daily: DailyForecast[];
}

interface ForecastResponse {
  current: {
    time: string;
    temperature_2m: number;
    apparent_temperature: number;
    weather_code: number;
    is_day: number;
    relative_humidity_2m: number;
    wind_speed_10m: number;
    wind_direction_10m: number;
  };
  hourly: { time: string[]; temperature_2m: number[]; weather_code: number[]; precipitation_probability: number[]; is_day: number[] };
  daily: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_probability_max: number[];
    uv_index_max: number[];
    sunrise: string[];
    sunset: string[];
  };
}

export async function fetchForecast(
  loc: Location,
  opts: Pick<Settings['weather'], 'unit' | 'windUnit'>,
  signal?: AbortSignal,
): Promise<Forecast> {
  const params = new URLSearchParams({
    latitude: String(loc.latitude),
    longitude: String(loc.longitude),
    current: 'temperature_2m,apparent_temperature,weather_code,is_day,relative_humidity_2m,wind_speed_10m,wind_direction_10m',
    hourly: 'temperature_2m,weather_code,precipitation_probability,is_day',
    daily: 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,uv_index_max,sunrise,sunset',
    timezone: 'auto',
    forecast_days: '7',
    forecast_hours: '30',
    temperature_unit: opts.unit,
    wind_speed_unit: opts.windUnit,
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`, { signal });
  if (!res.ok) throw new Error(`Weather unavailable (${res.status})`);
  const d = (await res.json()) as ForecastResponse;
  const at = <T,>(arr: T[] | undefined, i: number, fallback: T): T => arr?.[i] ?? fallback;
  // Hourly starts at midnight today: keep the hours from "now" on.
  const nowHour = d.current.time.slice(0, 13);
  return {
    current: {
      temperature: d.current.temperature_2m,
      feelsLike: d.current.apparent_temperature,
      code: d.current.weather_code,
      isDay: d.current.is_day === 1,
      humidity: d.current.relative_humidity_2m,
      wind: d.current.wind_speed_10m,
      windDirection: d.current.wind_direction_10m,
    },
    hourly: d.hourly.time
      .map((time, i) => ({
        time,
        temperature: at(d.hourly.temperature_2m, i, 0),
        code: at(d.hourly.weather_code, i, 0),
        rainChance: at(d.hourly.precipitation_probability, i, 0),
        isDay: at(d.hourly.is_day, i, 1) === 1,
      }))
      .filter((h) => h.time.slice(0, 13) >= nowHour)
      .slice(0, 24),
    daily: d.daily.time.map((date, i) => ({
      date,
      code: at(d.daily.weather_code, i, 0),
      max: at(d.daily.temperature_2m_max, i, 0),
      min: at(d.daily.temperature_2m_min, i, 0),
      rainChance: at(d.daily.precipitation_probability_max, i, 0),
      uv: at(d.daily.uv_index_max, i, 0),
      sunrise: at(d.daily.sunrise, i, ''),
      sunset: at(d.daily.sunset, i, ''),
    })),
  };
}

interface GeoResponse {
  results?: Array<{
    name: string;
    latitude: number;
    longitude: number;
    country?: string;
    country_code?: string;
    admin1?: string;
    admin2?: string;
  }>;
}

export async function searchPlaces(query: string): Promise<Location[]> {
  const params = new URLSearchParams({ name: query, count: '6', language: navigator.language.slice(0, 2) || 'en' });
  const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?${params}`);
  if (!res.ok) throw new Error('Place search failed');
  const d = (await res.json()) as GeoResponse;
  return (d.results ?? []).map((r) => ({
    name: [r.name, r.admin1, r.country].filter(Boolean).join(', '),
    latitude: r.latitude,
    longitude: r.longitude,
    countryCode: r.country_code?.toUpperCase(),
    region: [r.admin2, r.admin1].filter(Boolean).join(', ') || undefined,
  }));
}

export function currentPosition(): Promise<Location> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('Location is not available on this device'));
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          name: 'Current location',
          latitude: Math.round(p.coords.latitude * 100) / 100,
          longitude: Math.round(p.coords.longitude * 100) / 100,
        }),
      () => reject(new Error('Location permission was denied')),
      { timeout: 15_000, maximumAge: 3_600_000 },
    );
  });
}

/** WMO weather interpretation codes → short label. */
export function describeWeather(code: number): string {
  if (code === 0) return 'Clear';
  if (code <= 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if (code === 45 || code === 48) return 'Fog';
  if (code >= 51 && code <= 57) return 'Drizzle';
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return 'Rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'Snow';
  if (code >= 95) return 'Thunderstorm';
  return 'Unknown';
}

export const WIND_LABEL: Record<Settings['weather']['windUnit'], string> = { kmh: 'km/h', mph: 'mph', ms: 'm/s' };

export function compass(deg: number): string {
  return ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8] ?? 'N';
}
